import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatData } from "@/lib/api";
import { StatoBadge, ScadenzaBadge } from "@/components/Badges";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Save, Pencil } from "lucide-react";

const Campo = ({ label, children }) => (
  <div>
    <Label className="text-xs tracking-[0.05em] text-[#5C5F58]">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const onlyDate = (d) => (d ? d.slice(0, 10) : "");

const AssegnoDettaglio = ({ assegno, open, onOpenChange, onUpdated, onDeleted }) => {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (assegno) {
      setF({
        valore: assegno.valore ?? "",
        numero_assegno: assegno.numero_assegno || "",
        banca: assegno.banca || "",
        data_assegno: onlyDate(assegno.data_assegno),
        data_scadenza: onlyDate(assegno.data_scadenza),
        ricevuto_da: assegno.ricevuto_da || "",
        data_ricezione: onlyDate(assegno.data_ricezione),
        destinatario: assegno.destinatario || "",
        data_girata: onlyDate(assegno.data_girata),
        stato: assegno.stato || "ricevuto",
        note: assegno.note || "",
      });
    }
  }, [assegno]);

  if (!assegno) return null;

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const salva = async () => {
    setSaving(true);
    try {
      const payload = {
        valore: f.valore === "" ? null : parseFloat(f.valore),
        numero_assegno: f.numero_assegno || "",
        banca: f.banca || "",
        data_assegno: f.data_assegno || "",
        data_scadenza: f.data_scadenza || "",
        ricevuto_da: f.ricevuto_da || "",
        data_ricezione: f.data_ricezione || "",
        destinatario: f.destinatario || "",
        data_girata: f.data_girata || "",
        stato: f.stato,
        note: f.note || "",
      };
      const updated = await api.aggiorna(assegno.id, payload);
      toast.success("Assegno aggiornato");
      onUpdated && onUpdated(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const elimina = async () => {
    try {
      await api.elimina(assegno.id);
      toast.success("Assegno eliminato");
      onDeleted && onDeleted(assegno.id);
      onOpenChange(false);
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="dettaglio-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3">
            Dettaglio assegno
            <StatoBadge stato={assegno.stato} />
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <Pencil className="w-3.5 h-3.5" /> Tutti i campi sono modificabili: correggi i dati estratti se necessario.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Foto */}
          <div>
            <p className="text-xs tracking-[0.1em] uppercase text-[#5C5F58] mb-2">Foto assegno</p>
            {assegno.immagine ? (
              <img
                src={assegno.immagine}
                alt="Assegno"
                data-testid="dettaglio-immagine"
                className="w-full rounded-lg border border-[#E5E2DC] object-contain bg-[#F9F8F6]"
              />
            ) : (
              <div className="w-full h-40 rounded-lg border border-dashed border-[#E5E2DC] flex items-center justify-center text-sm text-[#5C5F58]">
                Nessuna foto
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="text-[#5C5F58]">Scadenza:</span>
              <ScadenzaBadge giorni={assegno.giorni_alla_scadenza} />
            </div>
          </div>

          {/* Dati estratti — modificabili */}
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Valore (€)">
              <Input type="number" step="0.01" data-testid="edit-valore" value={f.valore} onChange={(e) => upd("valore", e.target.value)} />
            </Campo>
            <Campo label="Numero assegno">
              <Input data-testid="edit-numero" value={f.numero_assegno} onChange={(e) => upd("numero_assegno", e.target.value)} />
            </Campo>
            <Campo label="Banca">
              <Input data-testid="edit-banca" value={f.banca} onChange={(e) => upd("banca", e.target.value)} />
            </Campo>
            <Campo label="Data assegno">
              <Input type="date" data-testid="edit-data-assegno" value={f.data_assegno} onChange={(e) => upd("data_assegno", e.target.value)} />
            </Campo>
            <Campo label="Scadenza">
              <Input type="date" data-testid="edit-data-scadenza" value={f.data_scadenza} onChange={(e) => upd("data_scadenza", e.target.value)} />
            </Campo>
            <Campo label="Ricevuto da">
              <Input data-testid="edit-ricevuto-da" value={f.ricevuto_da} onChange={(e) => upd("ricevuto_da", e.target.value)} />
            </Campo>
            <Campo label="Data ricezione">
              <Input type="date" data-testid="edit-data-ricezione" value={f.data_ricezione} onChange={(e) => upd("data_ricezione", e.target.value)} />
            </Campo>
          </div>
        </div>

        <div className="mt-2 space-y-4 border-t border-[#E5E2DC] pt-4">
          <p className="font-heading font-semibold text-sm">Girata & Tracciabilità</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Destinatario (a chi è stato girato)</Label>
              <Input
                data-testid="input-destinatario"
                value={f.destinatario}
                onChange={(e) => upd("destinatario", e.target.value)}
                placeholder="Es. Mario Rossi"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Data girata</Label>
              <Input
                type="date"
                data-testid="input-data-girata"
                value={f.data_girata}
                onChange={(e) => upd("data_girata", e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={f.stato} onValueChange={(v) => upd("stato", v)}>
                <SelectTrigger data-testid="select-stato" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ricevuto">Ricevuto</SelectItem>
                  <SelectItem value="girato">Girato</SelectItem>
                  <SelectItem value="incassato">Incassato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Note</Label>
              <Textarea
                data-testid="input-note"
                value={f.note}
                onChange={(e) => upd("note", e.target.value)}
                placeholder="Annotazioni..."
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="btn-elimina" className="text-[#B84223] border-[#FBE3DA] hover:bg-[#FBE3DA]">
                <Trash2 className="w-4 h-4 mr-1" /> Elimina
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare l'assegno?</AlertDialogTitle>
                <AlertDialogDescription>Questa azione è irreversibile.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={elimina} data-testid="btn-conferma-elimina" className="bg-[#B84223] hover:bg-[#9a3119]">
                  Elimina
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            onClick={salva}
            disabled={saving}
            data-testid="btn-salva-dettaglio"
            className="bg-[#1C3F35] hover:bg-[#132A23] text-white"
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssegnoDettaglio;
